import { useEffect, useState, useRef, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronDown,
  Calendar,
  Users,
  Building2,
  MapPin,
  User,
  Info,
  ClipboardList,
  FileText,
  MessageSquare,
  HelpCircle,
  Book,
  Settings,
  LogOut,
  LayoutDashboard,
  Building,
  Mail,
  Phone,
  Globe,
  Heart,
  Star,
  Award,
  Briefcase,
  Package,
  Shield,
  Key,
  Lock,
  Zap,
  Target,
  ArrowRight,
} from 'lucide-react'
import { Account } from '../../types/account.types'
import { EventOptions, NavItemOrder } from '../../types/eventDetails.types'
import { useCustomPages } from '../hooks/useCustomPages'
import { useResponsiveNavigation } from '../hooks/useResponsiveNavigation'
import { ConfirmationModal } from '@planner/bundles/main/admin/components/common/ConfirmationModal'
import { useParticipantCheck } from '../../hooks/useParticipantCheck'
import { AccountDropdown } from './shared/AccountDropdown'

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Map icon string values to Lucide icons
const getCustomPageIcon = (iconValue?: string) => {
  if (!iconValue) return FileText;

  const iconMap: Record<string, any> = {
    'file-text': FileText,
    'building': Building,
    'users': Users,
    'calendar': Calendar,
    'mail': Mail,
    'phone': Phone,
    'globe': Globe,
    'heart': Heart,
    'star': Star,
    'award': Award,
    'briefcase': Briefcase,
    'package': Package,
    'shield': Shield,
    'key': Key,
    'lock': Lock,
    'zap': Zap,
    'target': Target,
  };

  return iconMap[iconValue] || FileText;
};

// Default navigation configuration
const defaultMainNavigation = [
  {
    name: 'Details',
    href: '/details',
    icon: Info,
  },
  {
    name: 'Schedule',
    href: '/schedule',
    icon: Calendar,
  },
  {
    name: 'Registration',
    href: '/registration',
    icon: ClipboardList,
  },
]
const defaultMoreNavigation = [
  {
    name: 'Speakers',
    href: '/speakers',
    icon: Users,
    priority: 1,
  },
  {
    name: 'Expo',
    href: '/expo',
    icon: Building2,
    priority: 2,
  },
]
const getPageTitle = (pathname: string, customPages?: any[], eventOptions?: EventOptions) => {
  switch (pathname) {
    case '/':
    case '/details':
      return eventOptions?.detailsName || 'Details'
    case '/schedule':
      return eventOptions?.scheduleName || 'Schedule'
    case '/speakers':
      return eventOptions?.speakersName || 'Speakers'
    case '/expo':
      return eventOptions?.expoName || 'Expo'
    case '/resources':
      return 'Resources'
    case '/forum':
      return 'Forum'
    case '/faq':
      return 'FAQ'
    case '/docs':
      return 'Docs'
    case '/registration':
      return eventOptions?.registrationName || 'Registration'
    case '/forms':
      return eventOptions?.formsNavName || 'Forms'
    default:
      // Check if it's a custom page
      if (pathname.startsWith('/pages/') && customPages) {
        const slug = pathname.replace('/pages/', '')
        const page = customPages.find(p => p.slug === slug)
        return page?.title || 'Event'
      }
      // Check if it's a form page (including individual forms)
      if (pathname.startsWith('/forms')) {
        return eventOptions?.formsNavName || 'Forms'
      }
      return 'Event'
  }
}
const getPageIcon = (pathname: string, customPages?: any[]) => {
  switch (pathname) {
    case '/':
    case '/details':
      return Info
    case '/schedule':
      return Calendar
    case '/speakers':
      return Users
    case '/expo':
      return Building2
    case '/resources':
      return FileText
    case '/forum':
      return MessageSquare
    case '/faq':
      return HelpCircle
    case '/docs':
      return Book
    case '/registration':
      return ClipboardList
    case '/forms':
      return FileText
    default:
      // Check if it's a custom page
      if (pathname.startsWith('/pages/') && customPages) {
        const slug = pathname.replace('/pages/', '')
        const page = customPages.find(p => p.slug === slug)
        return getCustomPageIcon(page?.icon)
      }
      // Check if it's a form page (including individual forms)
      if (pathname.startsWith('/forms')) {
        return FileText
      }
      return Calendar
  }
}
interface NavbarProps {
  eventSlug: string;
  eventName?: string;
  eventDates?: string;
  account?: Account;
  accountLoading?: boolean;
  primaryColor?: string;
  eventOptions?: EventOptions;
  eventImageUrl?: string;
  isRegistrationContext?: boolean;
  registrationOpen?: boolean;
  onNavigationAttempt?: (href: string, isExternal?: boolean) => boolean;
}

// Navbar component
export const Navbar = ({ eventSlug, eventName, eventDates, account, accountLoading, primaryColor, eventOptions, eventImageUrl, isRegistrationContext = false, registrationOpen = false, onNavigationAttempt }: NavbarProps) => {
  const location = useLocation()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false)
  const [showRegistrationConfirmation, setShowRegistrationConfirmation] = useState(false)

  // Helper to get active nav item styles
  const getActiveNavStyles = (isActive: boolean) => {
    if (isActive) {
      return {
        className: 'text-white',
        style: { backgroundColor: primaryColor || '#2563eb' }
      };
    }
    return {
      className: 'text-gray-600 hover:text-gray-900',
      style: {}
    };
  }
  const moreMenuRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)

  // Get participant status
  const { data: participantCheck } = useParticipantCheck(eventSlug, !!account)
  const isParticipant = participantCheck?.isParticipant || false

  // Get registration button text
  const registrationButtonText = eventOptions?.registrationName || 'Registration'

  // Build navigation items based on eventOptions
  const { mainNavigation, moreNavigation } = useMemo(() => {
    if (!eventOptions) {
      return { mainNavigation: defaultMainNavigation, moreNavigation: defaultMoreNavigation }
    }

    const allNavItems = [
      {
        key: 'details',
        name: eventOptions.detailsName || 'Details',
        href: '/details',
        icon: Info,
        show: eventOptions.showDetailsNav !== false,
        isMain: true,
      },
      {
        key: 'schedule',
        name: eventOptions.scheduleName || 'Schedule',
        href: '/schedule',
        icon: Calendar,
        show: eventOptions.showScheduleNav !== false,
        isMain: true,
      },
      {
        key: 'speakers',
        name: eventOptions.speakersName || 'Speakers',
        href: '/speakers',
        icon: Users,
        show: eventOptions.showSpeakersNav !== false,
        isMain: false,
        priority: 1,
      },
      {
        key: 'expo',
        name: eventOptions.expoName || 'Expo',
        href: '/expo',
        icon: Building2,
        show: eventOptions.showExpoNav !== false,
        isMain: false,
        priority: 2,
      },
      {
        key: 'registration',
        name: eventOptions.registrationName || 'Registration',
        href: '/registration',
        icon: ClipboardList,
        show: eventOptions.showRegistrationNav !== false && registrationOpen,
        isMain: true,
      },
      {
        key: 'forms',
        name: eventOptions.formsNavName || 'Forms',
        href: '/forms',
        icon: FileText,
        show: eventOptions.showFormsNav !== false,
        isMain: false,
        priority: 3,
      },
    ]

    // Filter visible items
    const visibleItems = allNavItems.filter(item => item.show)

    // Apply custom order if provided
    let orderedItems = visibleItems
    if (eventOptions.navItemsOrder?.length) {
      const itemsMap = new Map(visibleItems.map(item => [item.key, item]))
      orderedItems = eventOptions.navItemsOrder
        .map(navOrder => {
          // Handle both object format and potential string format
          const itemKey = typeof navOrder === 'object' ? navOrder.item : navOrder
          return itemsMap.get(itemKey)
        })
        .filter(Boolean) as typeof visibleItems
    }

    // Separate main and more navigation based on navItemsOrder
    const main: typeof visibleItems = []
    const more: typeof visibleItems = []

    if (eventOptions.navItemsOrder?.length) {
      // Use the inMainNav flag from navItemsOrder, but allow up to 5 items in main nav
      orderedItems.forEach((item) => {
        // Find the corresponding navOrder item by key
        const navOrderItem = eventOptions.navItemsOrder?.find(
          order => order.item === item.key
        )
        const inMainNav = navOrderItem?.inMainNav ?? item.isMain

        // Allow up to 5 items in main navigation on desktop
        if (inMainNav && main.length < 5) {
          main.push(item)
        } else {
          more.push(item)
        }
      })
    } else {
      // Fallback to default behavior - take first 5 items for main nav
      orderedItems.forEach((item, index) => {
        if (index < 5) {
          main.push(item)
        } else {
          more.push(item)
        }
      })
    }

    return {
      mainNavigation: main.map(({ key, ...rest }) => rest),
      moreNavigation: more.map(({ key, ...rest }) => rest)
    }
  }, [eventOptions])

  // Fetch custom pages
  const { data: customPages } = useCustomPages(eventSlug)

  // Use responsive navigation hook (but only when we have eventOptions)
  const { visibleMainItems, dropdownItems, isCalculating } = useResponsiveNavigation({
    mainItems: mainNavigation,
    moreItems: moreNavigation,
    customPages
  })

  // On initial load before eventOptions, show the computed navigation directly
  const displayMainItems = !eventOptions ? mainNavigation : visibleMainItems
  const displayDropdownItems = !eventOptions ? moreNavigation : dropdownItems

  // Don't hide navigation when not calculating or when eventOptions not loaded
  const shouldHideNav = eventOptions && isCalculating

  // Show loading state while event options are being fetched
  const isInitialLoading = !eventOptions && !eventDates

  // Helper function to check if a nav item is active
  const isNavItemActive = (href: string): boolean => {
    // When in registration context, registration should always be highlighted
    if (isRegistrationContext && href === '/registration') {
      return true;
    }
    // Special case: Details tab should be active for both '/' and '/details'
    if (href === '/details' && location.pathname === '/') {
      return true;
    }
    // Special case: Forms tab should be active for any form page
    if (href === '/forms' && location.pathname.startsWith('/forms')) {
      return true;
    }
    return location.pathname === href;
  }

  // Helper function to handle navigation based on context
  const handleNavigation = (href: string, e?: React.MouseEvent) => {
    if (isRegistrationContext && href !== '/registration') {
      // In registration context, navigate to public event app for non-registration links
      if (e) e.preventDefault();

      // Call the navigation attempt callback if provided
      if (onNavigationAttempt) {
        const shouldProceed = onNavigationAttempt(href, false);
        if (!shouldProceed) {
          return; // Navigation was prevented
        }
      }

      window.location.href = `/${eventSlug}${href}`;
    }
    // For registration links or when not in registration context, use normal routing
  }

  // Helper function to handle registration button click when already in registration
  const handleRegistrationClick = (e: React.MouseEvent) => {
    if (isRegistrationContext && location.pathname !== '/') {
      // If we're already in registration and not on the initial page, show confirmation
      e.preventDefault();
      setShowRegistrationConfirmation(true);
    }
  }

  // Handle confirmation to start new registration
  const handleConfirmNewRegistration = () => {
    setShowRegistrationConfirmation(false);
    // Navigate to the registration root, which will start a new order
    window.location.href = `/${eventSlug}/register`;
  }
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        moreMenuRef.current &&
        !moreMenuRef.current.contains(event.target as Node) &&
        !moreButtonRef.current?.contains(event.target as Node)
      ) {
        setIsMoreMenuOpen(false)
      }
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMoreMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])
  const PageIcon = getPageIcon(location.pathname, customPages)
  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 responsive-nav-container">
        <div className="flex items-center h-16 relative">
          {/* Left section - Event info absolutely positioned with lower z-index */}
          <div className={`absolute left-0 top-1/2 -translate-y-1/2 flex items-center min-w-0 max-w-[200px] ${displayMainItems.length < 3 ? 'lg:max-w-[350px]' : 'lg:max-w-[280px]'} nav-left-content z-0`}>
            {isRegistrationContext ? (
              <a
                href={`/${eventSlug}`}
                className="flex items-center min-w-0 max-w-full"
                onClick={(e) => {
                  e.preventDefault();
                  if (onNavigationAttempt) {
                    const shouldProceed = onNavigationAttempt('/', true);
                    if (shouldProceed) {
                      window.location.href = `/${eventSlug}`;
                    }
                  } else {
                    window.location.href = `/${eventSlug}`;
                  }
                }}
              >
                {eventImageUrl ? (
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                    {/* Blurred background */}
                    <div className="absolute inset-0">
                      <img
                        src={eventImageUrl}
                        alt=""
                        className="w-full h-full object-cover blur-xl scale-110 opacity-20"
                        aria-hidden="true"
                      />
                    </div>
                    {/* Main image */}
                    <div className="relative h-full w-full flex items-center justify-center">
                      <img
                        src={eventImageUrl}
                        alt={eventName}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                ) : null}
                <div className="ml-4 hidden sm:flex flex-col justify-center min-w-0 overflow-hidden relative">
                  {isInitialLoading ? (
                    <>
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-1" />
                      <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                    </>
                  ) : (
                    <>
                      <div
                        className={`${(eventName?.length || 0) > 64 ? 'text-xs line-clamp-2' : 'text-sm truncate'} font-medium`}
                        title={eventName || 'Event'}
                      >
                        {eventName || 'Event'}
                      </div>
                      {eventDates && <div className="text-xs text-gray-500 truncate">{eventDates}</div>}
                    </>
                  )}
                </div>
              </a>
            ) : (
              <Link to="/details" className="flex items-center min-w-0 max-w-full">
                {eventImageUrl ? (
                  <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                    {/* Blurred background */}
                    <div className="absolute inset-0">
                      <img
                        src={eventImageUrl}
                        alt=""
                        className="w-full h-full object-cover blur-xl scale-110 opacity-20"
                        aria-hidden="true"
                      />
                    </div>
                    {/* Main image */}
                    <div className="relative h-full w-full flex items-center justify-center">
                      <img
                        src={eventImageUrl}
                        alt={eventName}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                ) : null}
                <div className="ml-3 hidden sm:flex flex-col justify-center min-w-0 overflow-hidden">
                  <div
                    className={`${(eventName?.length || 0) > 64 ? 'text-xs line-clamp-2' : 'text-sm truncate'} font-medium text-gray-900`}
                    title={eventName}
                  >
                    {eventName}
                  </div>
                  {eventDates && (
                    <div className="text-xs text-gray-500 truncate">{eventDates}</div>
                  )}
                </div>
              </Link>
            )}
          </div>

          {/* Center section - Navigation (desktop only) - truly centered with higher z-index */}
          <div className="hidden lg:flex items-center justify-center w-full z-10">
            <div className={`flex items-center gap-2 ${displayMainItems.length < 3 ? 'px-[380px]' : 'px-[320px]'}`}>
              {isInitialLoading ? (
                // Loading state - show skeleton navigation items
                <>
                  <div className="px-3 py-2 rounded-md">
                    <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="px-3 py-2 rounded-md">
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="px-3 py-2 rounded-md">
                    <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                  </div>
                </>
              ) : (
                displayMainItems.map((item) => {
                // Handle registration link differently based on context
                if (item.href === '/registration') {
                  if (isRegistrationContext) {
                    // In registration context, show confirmation if not on initial page
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={handleRegistrationClick}
                        className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isNavItemActive(item.href)
                            ? 'text-white'
                            : 'text-gray-600 hover:text-gray-900'
                        } ${shouldHideNav ? 'opacity-0' : 'opacity-100'}`}
                        style={isNavItemActive(item.href) ? {
                          backgroundColor: primaryColor || '#2563eb'
                        } : {}}
                      >
                        {item.name}
                      </Link>
                    );
                  } else {
                    // In public event context, open registration in new tab
                    return (
                      <a
                        key={item.name}
                        href={`/${eventSlug}/register`}
                        rel="noopener noreferrer"
                        className={`relative px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                          isNavItemActive(item.href)
                            ? 'text-white'
                            : 'text-gray-600 hover:text-gray-900'
                        } ${shouldHideNav ? 'opacity-0' : 'opacity-100'}`}
                        style={isNavItemActive(item.href) ? {
                          backgroundColor: primaryColor || '#2563eb'
                        } : {}}
                      >
                        {item.name}
                      </a>
                    );
                  }
                }

                // For non-registration links
                if (isRegistrationContext) {
                  // In registration context, use regular anchor tags for external navigation
                  return (
                    <a
                      key={item.name}
                      href={`/${eventSlug}${item.href}`}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isNavItemActive(item.href)
                          ? 'text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${shouldHideNav ? 'opacity-0' : 'opacity-100'}`}
                      style={isNavItemActive(item.href) ? {
                        backgroundColor: primaryColor || '#2563eb'
                      } : {}}
                      onClick={(e) => handleNavigation(item.href, e)}
                    >
                      {item.name}
                    </a>
                  );
                } else {
                  // In public event context, use React Router
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isNavItemActive(item.href)
                          ? 'text-white'
                          : 'text-gray-600 hover:text-gray-900'
                      } ${shouldHideNav ? 'opacity-0' : 'opacity-100'}`}
                      style={isNavItemActive(item.href) ? {
                        backgroundColor: primaryColor || '#2563eb'
                      } : {}}
                    >
                      {item.name}
                    </Link>
                  );
                }
              }))}
              {!isInitialLoading && (displayDropdownItems.length > 0 || (customPages && customPages.length > 0)) && (
                <div className="relative">
                  <button
                    ref={moreButtonRef}
                    onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-gray-900 ${shouldHideNav ? 'opacity-0' : 'opacity-100'}`}
                  >
                  More
                  <ChevronDown
                    size={16}
                    className={`transform transition-transform ${isMoreMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isMoreMenuOpen && (
                  <div
                    ref={moreMenuRef}
                    className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border z-50"
                  >
                    {displayDropdownItems.map((item) => {
                      // Handle registration link differently based on context
                      if (item.href === '/registration') {
                        if (isRegistrationContext) {
                          // In registration context, show confirmation if not on initial page
                          return (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center gap-2 px-4 py-2 text-sm ${isNavItemActive(item.href) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                              onClick={(e) => {
                                setIsMoreMenuOpen(false);
                                handleRegistrationClick(e);
                              }}
                            >
                              <item.icon size={16} />
                              {item.name}
                            </Link>
                          );
                        } else {
                          // In public event context, open registration in new tab
                          return (
                            <a
                              key={item.name}
                              href={`/${eventSlug}/register`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 px-4 py-2 text-sm ${isNavItemActive(item.href) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                              onClick={() => setIsMoreMenuOpen(false)}
                            >
                              <item.icon size={16} />
                              {item.name}
                            </a>
                          );
                        }
                      }

                      // For non-registration links
                      if (isRegistrationContext) {
                        // In registration context, use regular anchor tags
                        return (
                          <a
                            key={item.name}
                            href={`/${eventSlug}${item.href}`}
                            className={`flex items-center gap-2 px-4 py-2 text-sm ${isNavItemActive(item.href) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                            onClick={(e) => {
                              setIsMoreMenuOpen(false);
                              handleNavigation(item.href, e);
                            }}
                          >
                            <item.icon size={16} />
                            {item.name}
                          </a>
                        );
                      } else {
                        // In public event context, use React Router
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`flex items-center gap-2 px-4 py-2 text-sm ${isNavItemActive(item.href) ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                            onClick={() => setIsMoreMenuOpen(false)}
                          >
                            <item.icon size={16} />
                            {item.name}
                          </Link>
                        );
                      }
                    })}
                    {/* Add custom pages */}
                    {customPages && customPages.length > 0 && (
                      <>
                        {displayDropdownItems.length > 0 && <div className="border-t my-1" />}
                        {customPages.map((page) => {
                          if (isRegistrationContext) {
                            // In registration context, use regular anchor tags
                            return (
                              <a
                                key={page.slug}
                                href={`/${eventSlug}/pages/${page.slug}`}
                                className={`flex items-center gap-2 px-4 py-2 text-sm ${
                                  location.pathname === `/pages/${page.slug}`
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={(e) => {
                                  setIsMoreMenuOpen(false);
                                  handleNavigation(`/pages/${page.slug}`, e);
                                }}
                              >
                                {(() => {
                                  const CustomIcon = getCustomPageIcon(page.icon)
                                  return <CustomIcon size={16} />
                                })()}
                                {page.title}
                              </a>
                            );
                          } else {
                            // In public event context, use React Router
                            return (
                              <Link
                                key={page.slug}
                                to={`/pages/${page.slug}`}
                                className={`flex items-center gap-2 px-4 py-2 text-sm ${
                                  location.pathname === `/pages/${page.slug}`
                                    ? 'bg-blue-50 text-blue-700'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`}
                                onClick={() => setIsMoreMenuOpen(false)}
                              >
                                {(() => {
                                  const CustomIcon = getCustomPageIcon(page.icon)
                                  return <CustomIcon size={16} />
                                })()}
                                {page.title}
                              </Link>
                            );
                          }
                        })}
                      </>
                    )}
                  </div>
                )}
                </div>
              )}
            </div>
          </div>

          {/* Mobile navigation button - centered on mobile */}
          <div className="flex justify-center w-full lg:hidden">
            {isInitialLoading ? (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full">
                <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
              </div>
            ) : (
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-full transition-all"
              >
                <PageIcon size={18} />
                <span className="font-medium">
                  {getPageTitle(location.pathname, customPages, eventOptions)}
                </span>
                <ChevronDown
                  size={16}
                  className={`transform transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>
            )}
          </div>

          {/* Right section - Actions and account - absolutely positioned with higher z-index */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2 nav-right-content z-10">
            {/* Smart Action Button */}
            {!accountLoading && (
              <>
                {account ? (
                  // Signed-in user
                  isParticipant ? (
                    // Show Enter Event button for participants
                    <a
                      href={`/accounts/events/${eventSlug}`}
                      className="hidden sm:inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                      style={{ backgroundColor: primaryColor || '#2563eb' }}
                      onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                    >
                      Enter Event
                      <ArrowRight className="ml-1 h-4 w-4 flex-shrink-0" />
                    </a>
                  ) : registrationOpen ? (
                    // Show registration button for non-participants when registration is open
                    <a
                      href={`/${eventSlug}/register`}
                      className="hidden sm:inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                      style={{ backgroundColor: primaryColor || '#2563eb' }}
                      onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                    >
                      {registrationButtonText}
                      <ArrowRight className="ml-1 h-4 w-4 flex-shrink-0" />
                    </a>
                  ) : null
                ) : (
                  // Anonymous user - show registration if open
                  registrationOpen && (
                    <a
                      href={`/${eventSlug}/register`}
                      className="hidden sm:inline-flex items-center px-4 py-2 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                      style={{ backgroundColor: primaryColor || '#2563eb' }}
                      onMouseEnter={(e) => e.currentTarget.style.filter = 'brightness(0.9)'}
                      onMouseLeave={(e) => e.currentTarget.style.filter = 'brightness(1)'}
                    >
                      {registrationButtonText}
                      <ArrowRight className="ml-1 h-4 w-4 flex-shrink-0" />
                    </a>
                  )
                )}
              </>
            )}

            {/* Account Dropdown */}
            <AccountDropdown account={account} accountLoading={accountLoading} />
          </div>
        </div>
      </div>
      {isMobileMenuOpen && !isInitialLoading && (
        <div className="lg:hidden border-t">
          <div className="py-2 space-y-1">
            {/* Use the same ordered navigation as desktop */}
            {[...displayMainItems, ...displayDropdownItems].map((item) => {
              // Handle registration link differently based on context
              if (item.href === '/registration') {
                if (isRegistrationContext) {
                  // In registration context, show confirmation if not on initial page
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                        isNavItemActive(item.href) ? '' : 'hover:bg-gray-50'
                      }`}
                      style={isNavItemActive(item.href) && primaryColor ? {
                        backgroundColor: hexToRgba(primaryColor, 0.08),
                        color: primaryColor
                      } : {}}
                      onClick={(e) => {
                        setIsMobileMenuOpen(false);
                        handleRegistrationClick(e);
                      }}
                    >
                      <item.icon size={18} style={{ color: isNavItemActive(item.href) && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                      <div>
                        <div className="font-medium">{item.name}</div>
                      </div>
                    </Link>
                  );
                } else {
                  // In public event context, open registration in new tab
                  return (
                    <a
                      key={item.name}
                      href={`/${eventSlug}/register`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                        isNavItemActive(item.href) ? '' : 'hover:bg-gray-50'
                      }`}
                      style={isNavItemActive(item.href) && primaryColor ? {
                        backgroundColor: hexToRgba(primaryColor, 0.08),
                        color: primaryColor
                      } : {}}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <item.icon size={18} style={{ color: isNavItemActive(item.href) && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                      <div>
                        <div className="font-medium">{item.name}</div>
                      </div>
                    </a>
                  );
                }
              }

              // For non-registration links
              if (isRegistrationContext) {
                // In registration context, use regular anchor tags
                return (
                  <a
                    key={item.name}
                    href={`/${eventSlug}${item.href}`}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      isNavItemActive(item.href) ? '' : 'hover:bg-gray-50'
                    }`}
                    style={isNavItemActive(item.href) && primaryColor ? {
                      backgroundColor: hexToRgba(primaryColor, 0.08),
                      color: primaryColor
                    } : {}}
                    onClick={(e) => {
                      setIsMobileMenuOpen(false);
                      handleNavigation(item.href, e);
                    }}
                  >
                    <item.icon size={18} style={{ color: isNavItemActive(item.href) && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                    <div>
                      <div className="font-medium">{item.name}</div>
                    </div>
                  </a>
                );
              } else {
                // In public event context, use React Router
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                      isNavItemActive(item.href) ? '' : 'hover:bg-gray-50'
                    }`}
                    style={isNavItemActive(item.href) && primaryColor ? {
                      backgroundColor: hexToRgba(primaryColor, 0.08),
                      color: primaryColor
                    } : {}}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <item.icon size={18} style={{ color: isNavItemActive(item.href) && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                    <div>
                      <div className="font-medium">{item.name}</div>
                    </div>
                  </Link>
                );
              }
            })}
            {/* Add custom pages to mobile menu */}
            {customPages && customPages.length > 0 && (
              <>
                <div className="border-t border-gray-100 my-2" />
                {customPages.map((page) => {
                  if (isRegistrationContext) {
                    // In registration context, use regular anchor tags
                    return (
                      <a
                        key={page.slug}
                        href={`/${eventSlug}/pages/${page.slug}`}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          location.pathname === `/pages/${page.slug}` ? '' : 'hover:bg-gray-50'
                        }`}
                        style={location.pathname === `/pages/${page.slug}` && primaryColor ? {
                          backgroundColor: hexToRgba(primaryColor, 0.08),
                          color: primaryColor
                        } : {}}
                        onClick={(e) => {
                          setIsMobileMenuOpen(false);
                          handleNavigation(`/pages/${page.slug}`, e);
                        }}
                      >
                        {(() => {
                          const CustomIcon = getCustomPageIcon(page.icon)
                          return <CustomIcon size={18} style={{ color: location.pathname === `/pages/${page.slug}` && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                        })()}
                        <div>
                          <div className="font-medium">{page.title}</div>
                        </div>
                      </a>
                    );
                  } else {
                    // In public event context, use React Router
                    return (
                      <Link
                        key={page.slug}
                        to={`/pages/${page.slug}`}
                        className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                          location.pathname === `/pages/${page.slug}` ? '' : 'hover:bg-gray-50'
                        }`}
                        style={location.pathname === `/pages/${page.slug}` && primaryColor ? {
                          backgroundColor: hexToRgba(primaryColor, 0.08),
                          color: primaryColor
                        } : {}}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {(() => {
                          const CustomIcon = getCustomPageIcon(page.icon)
                          return <CustomIcon size={18} style={{ color: location.pathname === `/pages/${page.slug}` && primaryColor ? primaryColor : (primaryColor || '#2563eb') }} />
                        })()}
                        <div>
                          <div className="font-medium">{page.title}</div>
                        </div>
                      </Link>
                    );
                  }
                })}
              </>
            )}
          </div>
        </div>
      )}
      </nav>

      {/* Confirmation Modal for New Registration */}
      <ConfirmationModal
        isOpen={showRegistrationConfirmation}
        onClose={() => setShowRegistrationConfirmation(false)}
        onConfirm={handleConfirmNewRegistration}
        title={`Start New ${eventOptions?.registrationName || 'Registration'}?`}
        message={`You are currently in the middle of a ${(eventOptions?.registrationName || 'registration').toLowerCase()}. Starting a new ${(eventOptions?.registrationName || 'registration').toLowerCase()} will cancel your current progress. Are you sure you want to continue?`}
        confirmText={`Start New ${eventOptions?.registrationName || 'Registration'}`}
        cancelText={`Continue Current ${eventOptions?.registrationName || 'Registration'}`}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  )
}
